import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

import api from '../../../../services/api';
import { CODIGO, COR_CATEGORIA } from './estilo';

const ROTA = '/admin/snapshot';

function fmtValor(v) {
  if (v === true) return 'sim';
  if (v === false) return 'não';
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

/**
 * Texto cru: as primeiras linhas vem prontas, o resto sob demanda.
 *
 * O preview chega junto da listagem, entao da para reconhecer o que o comando
 * devolveu sem clique nenhum. So o texto completo custa uma ida ao servidor —
 * config de chassi grande tem milhares de linhas e nao cabe em toda listagem.
 */
function SaidaBruta({ saida, empresa }) {
  const [tudo, setTudo] = useState(null);
  const [aberto, setAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);

  const preview = saida.preview || '';
  const temMais = saida.raw_truncated;

  const expandir = async () => {
    const novoEstado = !aberto;
    setAberto(novoEstado);
    if (!novoEstado || tudo !== null) return;

    setCarregando(true);
    try {
      const res = await api.get(`${ROTA}/run/output/${saida.id}/raw`, { params: { empresa } });
      setTudo(res.data?.data?.raw || '');
    } catch {
      setTudo('');
    } finally {
      setCarregando(false);
    }
  };

  if (!preview && !temMais) {
    return (
      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
        sem saída guardada
      </Typography>
    );
  }

  const texto = aberto && tudo !== null ? tudo : preview;

  // Sem parser o texto e o UNICO conteudo do comando — escondê-lo atras de um
  // clique deixaria a linha vazia. Com parser, a tabela ja responde a pergunta e
  // o raw fica atras do proprio expand, para nao empurrar tudo para baixo.
  const semParser = !saida.entity_type;
  const mostrandoAlgo = semParser || aberto;

  return (
    <Box>
      <Stack
        direction="row" spacing={0.5} alignItems="center"
        sx={{ cursor: 'pointer', color: '#475569', width: 'fit-content', mb: 0.5 }}
        onClick={expandir}
      >
        {aberto ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
        <Typography variant="caption">
          {aberto
            ? 'ocultar saída bruta'
            : semParser
              ? `saída bruta — ${saida.raw_lines} linha(s)`
              : 'ver saída bruta'}
        </Typography>
        {carregando && <CircularProgress size={10} />}
      </Stack>

      {mostrandoAlgo && (
        <Box
          sx={{ ...CODIGO, maxHeight: aberto ? 340 : 'none', overflow: 'auto', whiteSpace: 'pre' }}
        >
          {texto || ' '}
        </Box>
      )}

      {semParser && !aberto && temMais && (
        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
          mostrando 5 de {saida.raw_lines} linhas
        </Typography>
      )}
    </Box>
  );
}

/** Tabela do que o parser extraiu de um comando. */
function Parseado({ saida }) {
  const entidades = saida.entities || [];

  if (saida.exec_error) {
    return (
      <Alert severity="error" sx={{ py: 0.5 }}>
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
          {saida.exec_error}
        </Typography>
      </Alert>
    );
  }

  if (saida.parse_status === 'error') {
    return (
      <Alert severity="error" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          O parser falhou: {saida.parse_error || 'sem detalhe'}. Nada deste comando
          entrou na comparação — a saída bruta está abaixo.
        </Typography>
      </Alert>
    );
  }

  // Sem parser nao rende aviso proprio por comando: repetido em cada linha vira
  // parede de texto azul e empurra para baixo justamente a saida, que nesse caso
  // e a UNICA coisa util. O aviso sobe para o host, uma vez so.
  if (!entidades.length) return null;

  // Une as chaves de todas as entidades: vendors diferentes trazem campos
  // diferentes, e fixar uma lista deixaria dado de fora sem avisar.
  const colunas = [...new Set(entidades.flatMap((e) => Object.keys(e.attrs || {})))];

  return (
    <Box sx={{ overflow: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, fontSize: 11.5, py: 0.5 }}>
              <Tooltip title="O lugar. Quando só isto muda entre as duas coletas, é uma migração — não um sumiço.">
                <span>lugar</span>
              </Tooltip>
            </TableCell>
            {colunas.map((c) => (
              <TableCell key={c} sx={{ fontWeight: 700, fontSize: 11.5, py: 0.5 }}>{c}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {entidades.map((e, i) => (
            <TableRow key={`${e.entity_key}-${i}`} hover>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, py: 0.4, color: '#7c3aed' }}>
                {e.location_key || '—'}
              </TableCell>
              {colunas.map((c) => (
                <TableCell key={c} sx={{ fontFamily: 'monospace', fontSize: 12, py: 0.4 }}>
                  {fmtValor((e.attrs || {})[c])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

/**
 * A coleta de um host: o conteudo extraido de cada comando, direto.
 *
 * A versao anterior listava os comandos ENVIADOS e escondia o conteudo atras de
 * um clique — mostrava o envelope no lugar da carta. O nome do comando e
 * metadado; o que o operador precisa ver as 3h da manha e o estado da rede que
 * aquele comando revelou.
 */
export default function ColetaDoHost({ runId, hostId, empresa }) {
  const [saidas, setSaidas] = useState(null);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    if (!runId || !hostId || !empresa) return undefined;
    let cancelado = false;
    setCarregando(true);

    api.get(`${ROTA}/run/${runId}/outputs`, { params: { empresa, host_id: hostId } })
      .then((res) => {
        if (!cancelado) setSaidas(res.data?.data || []);
      })
      .catch(() => { if (!cancelado) setSaidas([]); })
      .finally(() => { if (!cancelado) setCarregando(false); });

    return () => { cancelado = true; };
  }, [runId, hostId, empresa]);

  const total = useMemo(
    () => (saidas || []).reduce((a, s) => a + (s.entities?.length || 0), 0),
    [saidas]
  );
  const semParser = useMemo(
    () => (saidas || []).filter((s) => !s.entity_type).length,
    [saidas]
  );

  if (carregando && !saidas) {
    return <Box sx={{ py: 1 }}><CircularProgress size={16} /></Box>;
  }

  if (!saidas?.length) {
    return (
      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
        Nada gravado para este host.
      </Typography>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="caption" sx={{ color: '#64748b' }}>
          {saidas.length} comando(s), {total} item(ns) extraído(s)
        </Typography>
        {semParser > 0 && (
          <Tooltip title="Foram coletados e o texto está guardado, mas sem parser não viram dado estruturado e não entram na comparação.">
            <Chip
              size="small"
              label={`${semParser} sem parser`}
              sx={{ height: 18, bgcolor: '#e2e8f0', color: '#475569' }}
            />
          </Tooltip>
        )}
      </Stack>

      {saidas.map((s) => (
        <Box key={s.id} sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid #e2e8f0' }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }} flexWrap="wrap" useFlexGap>
            {s.entity_type ? (
              <Chip
                size="small" label={s.entity_type}
                sx={{
                  height: 20, color: '#fff', fontFamily: 'monospace',
                  bgcolor: COR_CATEGORIA[(s.entity_type || '').split('.')[0].toUpperCase()]
                    || COR_CATEGORIA.OUTROS,
                }}
              />
            ) : (
              <Chip size="small" label="sem parser"
                sx={{ height: 20, bgcolor: '#94a3b8', color: '#fff' }} />
            )}

            {!!s.entities?.length && (
              <Chip size="small" label={`${s.entities.length}`}
                sx={{ height: 20, bgcolor: '#16a085', color: '#fff', minWidth: 26 }} />
            )}

            {/* O comando que gerou isto continua disponível, mas como legenda —
                não como o assunto da linha. */}
            <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'monospace' }}>
              {s.command_text}
            </Typography>

            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              {s.duration_ms != null ? `${s.duration_ms} ms` : ''}
            </Typography>
          </Stack>

          <Parseado saida={s} />
          <SaidaBruta saida={s} empresa={empresa} />
        </Box>
      ))}
    </Box>
  );
}
